import { useCallback, useEffect, useRef, useState } from 'react';
import { Track } from 'livekit-client';
import { createLogger } from '@web-chat-core/toolkit';
import { getParticipantVideoElement } from './videoElementRegistry';

const logger = createLogger({ prefix: 'Snapshot' });

const SNAPSHOT_QUALITY = 0.6; // JPEG качество (0.6 = хороший баланс размер/качество)

// Храним snapshots в памяти (очищается при перезагрузке/выходе из комнаты)
// Map<participantId, dataUrl>
const snapshotsCache = new Map<string, string>();

interface UseVideoSnapshotOptions {
    participantId: string;
    track?: Track;
    isShowVideo?: boolean;
    isCameraEnabled?: boolean;
    isVideoReady?: boolean;
    videoElement?: HTMLVideoElement | null;
}

const captureSnapshotDataUrl = (participantId: string, videoElement: HTMLVideoElement): string | null => {
    try {
        if (videoElement.readyState < 2) {
            return null;
        }

        const videoWidth = videoElement.videoWidth;
        const videoHeight = videoElement.videoHeight;

        if (!videoWidth || !videoHeight) {
            return null;
        }

        const videoAspect = videoWidth / videoHeight;
        const targetAspect = 16 / 9;

        const canvas = document.createElement('canvas');
        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = videoWidth;
        let sourceHeight = videoHeight;

        if (videoAspect > targetAspect) {
            sourceWidth = videoHeight * targetAspect;
            sourceX = (videoWidth - sourceWidth) / 2;
        } else if (videoAspect < targetAspect) {
            sourceHeight = videoWidth / targetAspect;
            sourceY = (videoHeight - sourceHeight) / 2;
        }

        canvas.width = Math.round(sourceWidth);
        canvas.height = Math.round(sourceHeight);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            logger.warn(`Failed to get canvas context for ${participantId}`);
            return null;
        }

        ctx.drawImage(videoElement, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height);

        return canvas.toDataURL('image/jpeg', SNAPSHOT_QUALITY);
    } catch (error) {
        logger.warn(`Failed to capture snapshot for ${participantId}:`, error);
        return null;
    }
};

/**
 * Захватить snapshot из конкретного video-элемента и сохранить в кэш
 */
export const captureParticipantSnapshotFromElement = (
    participantId: string,
    videoElement: HTMLVideoElement,
): boolean => {
    const dataUrl = captureSnapshotDataUrl(participantId, videoElement);

    if (!dataUrl) {
        return false;
    }

    snapshotsCache.set(participantId, dataUrl);
    return true;
};

/**
 * Захватить snapshot участника по participantId (через video registry).
 * Вызывать до отписки от видеопотока, пока VideoRenderer ещё держит элемент.
 */
export const captureParticipantSnapshot = (participantId: string): boolean => {
    const videoElement = getParticipantVideoElement(participantId);

    if (!videoElement) {
        return false;
    }

    return captureParticipantSnapshotFromElement(participantId, videoElement);
};

/**
 * Хук для управления snapshots видеопотоков участников
 *
 * - Обновляет snapshot когда видео готово (для preview после resubscribe)
 * - При выключении камеры — сносит snapshot (показываем аватар)
 * - Capture перед scroll-unsubscribe — в useParticipantSubscriptions
 * - Session-cache по participantId на время комнаты
 */
export const useVideoSnapshot = ({
    participantId,
    track,
    isShowVideo = false,
    isCameraEnabled = true,
    isVideoReady,
    videoElement,
}: UseVideoSnapshotOptions) => {
    const captureTimeoutRef = useRef<number | null>(null);
    const savedVideoElementRef = useRef<HTMLVideoElement | null>(null);
    const [, setSnapshotVersion] = useState(0);

    const snapshot = snapshotsCache.get(participantId) || null;

    useEffect(() => {
        if (videoElement) {
            savedVideoElementRef.current = videoElement;
        }
    }, [videoElement]);

    const saveSnapshot = useCallback(
        (dataUrl: string) => {
            const previousSnapshot = snapshotsCache.get(participantId);
            snapshotsCache.set(participantId, dataUrl);

            if (previousSnapshot !== dataUrl) {
                setSnapshotVersion((version) => version + 1);
            }
        },
        [participantId],
    );

    const captureSnapshot = useCallback(
        (targetVideoElement: HTMLVideoElement) => {
            const dataUrl = captureSnapshotDataUrl(participantId, targetVideoElement);

            if (dataUrl) {
                saveSnapshot(dataUrl);
            }
        },
        [participantId, saveSnapshot],
    );

    const captureSnapshotRef = useRef(captureSnapshot);
    useEffect(() => {
        captureSnapshotRef.current = captureSnapshot;
    }, [captureSnapshot]);

    const getVideoElement = useCallback((): HTMLVideoElement | null => {
        return videoElement || savedVideoElementRef.current || getParticipantVideoElement(participantId);
    }, [videoElement, participantId]);

    const captureFromVideoElement = useCallback(() => {
        const targetVideo = getVideoElement();

        if (targetVideo && targetVideo.readyState >= 2) {
            captureSnapshotRef.current(targetVideo);
        }
    }, [getVideoElement]);

    // Камера выключена — снапшот не нужен, показываем аватар
    useEffect(() => {
        if (isCameraEnabled) {
            return;
        }

        if (snapshotsCache.has(participantId)) {
            snapshotsCache.delete(participantId);
            setSnapshotVersion((version) => version + 1);
        }
    }, [isCameraEnabled, participantId]);

    // Обновляем snapshot после загрузки видео (камера включена)
    useEffect(() => {
        if (!track || !isShowVideo || !isCameraEnabled || !isVideoReady) {
            return;
        }

        captureTimeoutRef.current = window.setTimeout(() => {
            captureFromVideoElement();
        }, 100);

        return () => {
            if (captureTimeoutRef.current) {
                clearTimeout(captureTimeoutRef.current);
                captureTimeoutRef.current = null;
            }
        };
    }, [track, track?.sid, isShowVideo, isCameraEnabled, isVideoReady, captureFromVideoElement]);

    return {
        snapshot,
        captureSnapshot,
    };
};

/**
 * Очистить snapshot для конкретного участника (leave / mute / ended)
 */
export const clearParticipantSnapshot = (participantId: string) => {
    snapshotsCache.delete(participantId);
};

/**
 * Удалить snapshots участников, которых уже нет в комнате
 */
export const clearAbsentParticipantSnapshots = (activeParticipantIds: Iterable<string>) => {
    const activeIds = new Set(activeParticipantIds);

    for (const participantId of snapshotsCache.keys()) {
        if (!activeIds.has(participantId)) {
            snapshotsCache.delete(participantId);
        }
    }
};

/**
 * Очистить все snapshots (вызывается при выходе из комнаты)
 */
export const clearAllSnapshots = () => {
    snapshotsCache.clear();
};

// re-export registry helpers for subscriptions / renderer
export { getParticipantVideoElement, subscribeParticipantVideoElements } from './videoElementRegistry';
